import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEO from '../components/SEO/SEO';

// ── Statistikos animacija ─────────────────────────────────────────────────────

function AnimatedCounter({ target, suffix = '' }) {
  // Render the real value directly so prerendered HTML is always accurate.
  return <span>{target.toLocaleString('lt')}{suffix}</span>;
}

// ── Žaidimo preview kortelė ───────────────────────────────────────────────────

function GamePreviewCard({
  icon,
  name,
  color,
  delay = 0,
  href,
}) {
  const card = (
    <div
      className="rounded-3xl overflow-hidden shadow-xl cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl"
      style={{ animation: `slideUp 0.5s ease-out ${delay}s both` }}
    >
      <div
        className={`bg-gradient-to-br ${color} p-6 text-white text-center`}
      >
        <div className="text-5xl mb-2">{icon}</div>

        <p className="font-black text-sm">
          {name}
        </p>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link
        to={href}
        aria-label={`${name} – SmartPanda mokomieji žaidimai`}
      >
        {card}
      </Link>
    );
  }

  return card;
}

// ── Atsiliepimų kortelė ───────────────────────────────────────────────────────

function TestimonialCard({ avatar, name, role, text, rating = 5 }) {
  return (
    <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-gray-100">
      <div className="flex mb-3">
        {Array.from({ length: rating }).map((_, i) => (
          <span key={i} className="text-yellow-400 text-lg">
            ⭐
          </span>
        ))}
      </div>

      <p className="text-gray-700 font-semibold mb-4 leading-relaxed">
        "{text}"
      </p>

      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-2xl"
          style={{ background: '#F0FFF0' }}
        >
          {avatar}
        </div>

        <div>
          <p className="font-black text-gray-800 text-sm">{name}</p>
          <p className="text-xs text-gray-400 font-bold">{role}</p>
        </div>
      </div>
    </div>
  );
}

// ── FAQ Accordion ─────────────────────────────────────────────────────────────

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-2 border-gray-100 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-6 py-4 flex justify-between items-center font-black text-gray-800 hover:bg-gray-50 transition"
        aria-expanded={open}
      >
        <span>{q}</span>

        <span
          className="text-2xl transition-transform duration-200"
          style={{
            transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
          aria-hidden="true"
        >
          +
        </span>
      </button>

      {open && (
        <div className="px-6 pb-4 text-gray-600 font-semibold leading-relaxed border-t-2 border-gray-100 pt-3 animate-fade-in">
          {a}
        </div>
      )}
    </div>
  );
}

// ── PAGRINDINIS KOMPONENTAS ───────────────────────────────────────────────────

const LandingPage = () => {
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const el = document.getElementById('stats-section');

    if (el) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  const games = [
    {
      icon: '🔢',
      name: 'Matematikos žaidimai',
      color: 'from-purple-500 to-pink-500',
      href: '/matematikos-zaidimai',
    },
    {
      icon: '📖',
      name: 'Skaitymo žaidimai',
      color: 'from-green-500 to-teal-500',
      href: '/skaitymo-zaidimai',
    },
    {
      icon: '🗣️',
      name: 'Logopediniai žaidimai',
      color: 'from-orange-500 to-red-500',
      href: '/logopediniai-zaidimai',
    },
    {
      icon: '🔤',
      name: 'Lietuvių kalbos žaidimai',
      color: 'from-yellow-500 to-orange-500',
      href: '/lietuviu-kalbos-zaidimai',
    },
    {
      icon: '🌿',
      name: 'Gamtos žaidimai',
      color: 'from-blue-500 to-indigo-500',
    },
    {
      icon: '🎵',
      name: 'Muzikos žaidimai',
      color: 'from-pink-500 to-rose-500',
    },
  ];

  const faqs = [
    {
      q: 'Ar SmartPanda tinka pradedantiesiems?',
      a: 'Taip! SmartPanda pritaikyta vaikams nuo 4 iki 14 metų. AI automatiškai prisitaiko prie vaiko lygio.',
    },
    {
      q: 'Kaip veikia nemokamas planas?',
      a: 'Nemokamas planas suteikia prieigą prie pagrindinių žaidimų ir riboto skaičiaus AI užklausų. Jokios kreditinės kortelės nereikia.',
    },
    {
      q: 'Ar galiu pridėti kelis vaikus?',
      a: 'Taip! Priklausomai nuo pasirinkto plano, galite pridėti kelis vaikų profilius su atskiromis mokymosi pažangos ataskaitomis.',
    },
    {
      q: 'Ar žaidimai saugūs vaikams?',
      a: 'SmartPanda sukurta vaikams skirtam mokymuisi. AI generuojamas turinys yra filtruojamas, o tėvai gali stebėti vaiko mokymosi veiklą.',
    },
    {
      q: 'Ar galiu atšaukti prenumeratą?',
      a: 'Taip, prenumeratą galite atšaukti bet kuriuo metu. Nėra ilgalaikių įsipareigojimų.',
    },
  ];

  return (
    <>
      <SEO
        title="SmartPanda – Mokomieji žaidimai vaikams su AI"
        description="Interaktyvūs mokomieji žaidimai vaikams nuo 4 iki 14 metų. Mokykitės matematikos, lietuvių kalbos, skaitymo ir kitų dalykų žaisdami su AI."
        canonical="/"
      />

      <div
        style={{
          fontFamily: 'Nunito, sans-serif',
          background: '#F7F7F7',
          color: '#1F1F1F',
        }}
      >
        {/* ── HEADER ────────────────────────────────────────────────── */}

        <header
          className="sticky top-0 z-50 px-4 py-3 shadow-sm"
          style={{
            background: 'white',
            borderBottom: '2px solid #E5E5E5',
          }}
        >
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            <Link
              to="/"
              className="flex items-center gap-2 hover:opacity-80 transition"
              aria-label="SmartPanda pagrindinis puslapis"
            >
              <span className="text-3xl" aria-hidden="true">
                🐼
              </span>

              <span className="text-xl font-black text-gray-800">
                SmartPanda
              </span>
            </Link>

            <nav
              className="hidden md:flex items-center gap-6"
              aria-label="Pagrindinė navigacija"
            >
              <a
                href="#features"
                className="font-bold text-gray-600 hover:text-green-600 transition text-sm"
              >
                Funkcijos
              </a>

              <a
                href="#games"
                className="font-bold text-gray-600 hover:text-green-600 transition text-sm"
              >
                Žaidimai
              </a>

              <Link
                to="/subscription"
                className="font-bold text-gray-600 hover:text-green-600 transition text-sm"
              >
                Kainos
              </Link>
            </nav>

            <div className="flex gap-3">
              <Link
                to="/login"
                className="px-4 py-2 rounded-xl font-black text-sm border-2 border-gray-200 text-gray-700 hover:border-gray-300 transition"
              >
                Prisijungti
              </Link>

              <Link
                to="/register"
                className="px-4 py-2 rounded-xl font-black text-sm text-white transition"
                style={{
                  background: '#58CC02',
                  boxShadow: '0 4px 0 #46A302',
                }}
              >
                Pradėti nemokamai
              </Link>
            </div>
          </div>
        </header>

        {/* ── HERO ──────────────────────────────────────────────────── */}

        <section
          className="relative overflow-hidden px-4 py-20 md:py-28"
          style={{
            background:
              'linear-gradient(135deg, #667eea 0%, #764ba2 60%, #f093fb 100%)',
          }}
        >
          {/* Decorative orbs */}

          <div
            className="absolute top-10 left-10 w-48 h-48 rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle, #fff, transparent)',
            }}
            aria-hidden="true"
          />

          <div
            className="absolute bottom-10 right-10 w-64 h-64 rounded-full opacity-15"
            style={{
              background:
                'radial-gradient(circle, #FFC800, transparent)',
            }}
            aria-hidden="true"
          />

          <div className="max-w-4xl mx-auto text-center relative z-10">
            {/* Social proof badge */}

            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-black mb-6"
              style={{
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
              }}
            >
              <span aria-hidden="true">⭐</span>
              <span>Žaismingas mokymasis su SmartPanda</span>
            </div>

            {/* Heading */}

            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              Mokomieji žaidimai vaikams
              <br />

              <span
                style={{
                  background:
                    'linear-gradient(90deg, #FFC800, #FF9600)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                mokykis žaisdamas su AI!
              </span>{' '}

              <span
                className="inline-block hover:scale-110 transition-transform"
                aria-hidden="true"
              >
                🐼
              </span>
            </h1>

            <p className="text-xl text-purple-100 mb-10 max-w-2xl mx-auto font-semibold leading-relaxed">
              Interaktyvūs mokomieji žaidimai vaikams nuo 4 iki 14 metų.
              Mokykitės matematikos, lietuvių kalbos, skaitymo, logopedijos
              ir kitų dalykų, o AI pritaikys užduotis pagal kiekvieno vaiko
              lygį.
            </p>

            {/* CTAs */}

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/register"
                className="px-8 py-4 rounded-2xl font-black text-lg text-gray-800 transition-all hover:scale-105"
                style={{
                  background: '#FFC800',
                  boxShadow: '0 6px 0 #E5B400',
                  minWidth: 220,
                }}
              >
                🚀 Pradėti nemokamai
              </Link>

              <Link
                to="/login"
                className="px-8 py-4 rounded-2xl font-black text-lg text-white transition-all hover:scale-105"
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '2px solid rgba(255,255,255,0.4)',
                  minWidth: 200,
                }}
              >
                👋 Turiu paskyrą
              </Link>
            </div>

            {/* Micro-copy */}

            <p className="mt-4 text-purple-200 text-sm font-semibold">
              ✓ Nemokamai &nbsp;·&nbsp; ✓ Jokios kreditinės kortelės
              &nbsp;·&nbsp; ✓ Pradėk iš karto
            </p>
          </div>
        </section>

        {/* ── SMARTPANDA FACTS ───────────────────────────────────────── */}

        <section
          id="stats-section"
          className="py-12 bg-white border-b-2 border-gray-100"
        >
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                {
                  target: 59,
                  suffix: '',
                  label: 'Mokomieji žaidimai',
                  icon: '🎮',
                },
                {
                  target: 4,
                  suffix: '–14',
                  label: 'Metų vaikams',
                  icon: '👧',
                },
                {
                  target: 3,
                  suffix: '',
                  label: 'Sudėtingumo lygiai',
                  icon: '⭐',
                },
              ].map((s) => (
                <div key={s.label} className="p-4">
                  <div className="text-3xl mb-2" aria-hidden="true">
                    {s.icon}
                  </div>

                  <div
                    className="text-4xl font-black"
                    style={{ color: '#58CC02' }}
                  >
                    <AnimatedCounter
                      target={s.target}
                      suffix={s.suffix}
                    />
                  </div>

                  <p className="text-gray-500 font-bold text-sm mt-1">
                    {s.label}
                  </p>
                </div>
              ))}

              <div className="p-4">
                <div className="text-3xl mb-2" aria-hidden="true">
                  🤖
                </div>

                <div
                  className="text-4xl font-black"
                  style={{ color: '#58CC02' }}
                >
                  AI
                </div>

                <p className="text-gray-500 font-bold text-sm mt-1">
                  Personalizuotos užduotys
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── KAIP TAI VEIKIA ───────────────────────────────────────── */}

        <section id="features" className="py-20 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-4xl font-black text-gray-800 mb-3">
                Kaip SmartPanda padeda vaikams mokytis?
              </h2>

              <p className="text-gray-500 font-semibold text-lg">
                Paprastas ir žaismingas mokymosi procesas vaikams ir tėvams
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: '1',
                  icon: '👤',
                  title: 'Sukurk paskyrą',
                  desc: 'Registruokitės per kelias sekundes. Pridėkite vaiko profilį su amžiumi ir klase.',
                  color: '#1CB0F6',
                },
                {
                  step: '2',
                  icon: '🎮',
                  title: 'Žaisk ir mokykis',
                  desc: 'AI generuoja unikalias užduotis pagal vaiko lygį. Kiekvieną dieną laukia nauji iššūkiai!',
                  color: '#58CC02',
                },
                {
                  step: '3',
                  icon: '📊',
                  title: 'Stebėk pažangą',
                  desc: 'Tėvai mato išsamias ataskaitas: mokymosi progresą, streak, pasiekimus ir stipriąsias sritis.',
                  color: '#FF9600',
                },
              ].map((s, i) => (
                <div
                  key={i}
                  className="bg-white rounded-3xl p-8 shadow-lg border-2 border-gray-100 text-center hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-xl mx-auto mb-4"
                    style={{ background: s.color }}
                  >
                    {s.step}
                  </div>

                  <div className="text-5xl mb-4" aria-hidden="true">
                    {s.icon}
                  </div>

                  <h3 className="text-xl font-black text-gray-800 mb-2">
                    {s.title}
                  </h3>

                  <p className="text-gray-500 font-semibold leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── GAMIFICATION SHOWCASE ─────────────────────────────────── */}

        <section
          className="py-20 px-4"
          style={{
            background:
              'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          }}
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-white mb-3">
                Žaidybinis mokymasis vaikams
              </h2>

              <p className="text-gray-400 font-semibold text-lg">
                XP, pasiekimai ir kasdieniai iššūkiai padeda išlaikyti vaikų
                motyvaciją
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {
                  icon: '🔥',
                  title: 'Streak sistema',
                  desc: 'Žaisk kiekvieną dieną ir didink seriją',
                  color: '#FF9600',
                },
                {
                  icon: '⭐',
                  title: 'XP taškai',
                  desc: 'Kaupi patirtį ir kylki lygiais',
                  color: '#FFC800',
                },
                {
                  icon: '🏅',
                  title: 'Pasiekimai',
                  desc: "18+ unikalių badge'ų atrakinti",
                  color: '#CE82FF',
                },
                {
                  icon: '🏆',
                  title: 'Lyga',
                  desc: 'Varžykis su kitais šią savaitę',
                  color: '#1CB0F6',
                },
              ].map((f, i) => (
                <div
                  key={i}
                  className="rounded-3xl p-5 text-center"
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <div className="text-4xl mb-3" aria-hidden="true">
                    {f.icon}
                  </div>

                  <h3 className="font-black text-white text-sm mb-1">
                    {f.title}
                  </h3>

                  <p
                    className="text-xs font-semibold"
                    style={{ color: f.color }}
                  >
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── ŽAIDIMŲ PREVIEW ───────────────────────────────────────── */}

        <section id="games" className="py-20 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-gray-800 mb-3">
                Mokomieji žaidimai vaikams
              </h2>

              <p className="text-gray-500 font-semibold max-w-3xl mx-auto leading-relaxed">
                Matematikos, skaitymo, lietuvių kalbos ir kalbos lavinimo žaidimai
                vaikams nuo 4 iki 14 metų. 59 mokomieji žaidimai, trys sudėtingumo lygiai
                ir AI pritaikomos užduotys.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {games.map((g, i) => (
                <GamePreviewCard
                  key={i}
                  {...g}
                  delay={i * 0.08}
                />
              ))}
            </div>

            <div className="text-center space-y-4">
              <Link
                to="/mokomieji-zaidimai"
                className="inline-block px-8 py-4 rounded-2xl font-black text-white text-lg transition-all hover:scale-105"
                style={{
                  background: '#58CC02',
                  boxShadow: '0 6px 0 #46A302',
                }}
              >
                Peržiūrėti visus mokomuosius žaidimus →
              </Link>

              <div>
                <Link
                  to="/register"
                  className="inline-block font-black text-green-600 hover:text-green-700 transition"
                >
                  Arba pradėkite žaisti nemokamai →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ── TEMATINIAI MOKYMOSI HUB'AI ─────────────────────────────── */}

        <section
          className="py-16 px-4"
          style={{ background: '#F7F7F7' }}
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-black text-gray-800 mb-3">
                Atraskite pagal mokymosi sritį
              </h2>

              <p className="text-gray-500 font-semibold max-w-3xl mx-auto leading-relaxed">
                Pasirinkite mokymosi sritį ir raskite vaikui tinkamus
                interaktyvius žaidimus bei užduotis.
              </p>
            </div>

            <nav
              aria-label="Mokymosi sritys"
              className="grid grid-cols-2 md:grid-cols-3 gap-4"
            >
              {[
                {
                  name: 'Loginio mąstymo žaidimai',
                  href: '/loginis-mastymas',
                  icon: '🧠',
                },
                {
                  name: 'Skaičių ir skaičiavimo žaidimai',
                  href: '/skaiciai-ir-skaiciavimas',
                  icon: '🔢',
                },
                {
                  name: 'Žodyno žaidimai',
                  href: '/zodynas',
                  icon: '📚',
                },
                {
                  name: 'Teksto supratimo žaidimai',
                  href: '/teksto-supratimas',
                  icon: '📖',
                },
                {
                  name: 'Dėmesio lavinimo žaidimai',
                  href: '/demesys',
                  icon: '🎯',
                },
                {
                  name: 'Mokymosi gidai tėvams',
                  href: '/mokymosi-gidai',
                  icon: '💡',
                },
              ].map((hub) => (
                <Link
                  key={hub.href}
                  to={hub.href}
                  className="group flex items-center gap-3 p-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <span
                    className="text-2xl shrink-0"
                    aria-hidden="true"
                  >
                    {hub.icon}
                  </span>

                  <span className="font-black text-gray-700 group-hover:text-green-600 transition">
                    {hub.name}
                  </span>
                </Link>
              ))}
            </nav>
          </div>
        </section>

        {/* ── TĖVŲ ATSILIEPIMAI ─────────────────────────────────────── */}

        <section
          className="py-20 px-4"
          style={{ background: '#F7F7F7' }}
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-gray-800 mb-3">
                Tėvai myli SmartPanda
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <TestimonialCard
                avatar="👩"
                name="Laura K."
                role="Dviejų vaikų mama, Vilnius"
                text="Mano sūnus per savaitę išmoko daugybos lentelę žaisdamas! Anksčiau tai buvo tikras iššūkis. Streak sistema jį motyvuoja kiekvieną dieną."
              />

              <TestimonialCard
                avatar="👨"
                name="Tomas M."
                role="Tėvas, Kaunas"
                text="Geriausias sprendimas lietuvių kalbos pratyboms. Duktė pati prašo papildomo laiko su SmartPanda. Vaiko progresas matomas tėvų dashboarde."
              />

              <TestimonialCard
                avatar="👩‍🏫"
                name="Rasa J."
                role="Pradinių klasių mokytoja"
                text="Rekomenduoju tėvams kaip namų darbų pagalbininkę. AI generuojami testai puikiai papildo mokyklinę programą ir skatina savarankišką mokymąsi."
              />
            </div>
          </div>
        </section>

        {/* ── KAINOS ────────────────────────────────────────────────── */}

        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-gray-800 mb-3">
                SmartPanda mokymosi planai
              </h2>

              <p className="text-gray-500 font-semibold">
                Pradėkite nemokamai, augkite kartu su vaikais
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                {
                  name: 'Nemokamas',
                  price: '€0',
                  period: 'visada',
                  color: '#AFAFAF',
                  features: [
                    '5+ žaidimų',
                    '2 AI užklausos/dieną',
                    '1 vaikas',
                    'Bazinė pažangos ataskaita',
                  ],
                  cta: 'Pradėti nemokamai',
                  ctaStyle: {
                    background: '#E5E5E5',
                    color: '#5C5C5C',
                  },
                  popular: false,
                },
                {
                  name: 'Basic',
                  price: '€12.99',
                  period: '/mėn',
                  color: '#58CC02',
                  features: [
                    '40+ žaidimų',
                    '25 AI užklausos/dieną',
                    '2 vaikai',
                    'Personalizuotos užduotys',
                    'XP ir streak sistema',
                  ],
                  cta: 'Pasirinkti Basic',
                  ctaStyle: {
                    background: '#58CC02',
                    color: 'white',
                    boxShadow: '0 4px 0 #46A302',
                  },
                  popular: true,
                },
                {
                  name: 'Premium',
                  price: '€24.99',
                  period: '/mėn',
                  color: '#CE82FF',
                  features: [
                    '59 mokomieji žaidimai',
                    '100 AI užklausų/dieną',
                    '5 vaikai',
                    'AI pasakojimų kūrėjas',
                    'Prioritetinis palaikymas',
                  ],
                  cta: 'Pasirinkti Premium',
                  ctaStyle: {
                    background: '#CE82FF',
                    color: 'white',
                    boxShadow: '0 4px 0 #A560D9',
                  },
                  popular: false,
                },
                {
                  name: 'Family',
                  price: '€49.99',
                  period: '/mėn',
                  color: '#CE82FF',
                  features: [
                    'Visi 59 mokomieji žaidimai',
                    'Neribotos AI užklausos/dieną',
                    '15 vaikų',
                    'AI pasakojimų kūrėjas',
                    'Prioritetinis palaikymas',
                  ],
                  cta: 'Pasirinkti Family',
                  ctaStyle: {
                    background: '#CE82FF',
                    color: 'white',
                    boxShadow: '0 4px 0 #A560D9',
                  },
                  popular: false,
                },
              ].map((plan, i) => (
                <div
                  key={i}
                  className="rounded-3xl p-7 border-2 relative"
                  style={{
                    borderColor: plan.popular
                      ? plan.color
                      : '#E5E5E5',
                    background: plan.popular ? '#F0FFF0' : 'white',
                    transform: plan.popular
                      ? 'scale(1.04)'
                      : 'none',
                    boxShadow: plan.popular
                      ? '0 8px 32px rgba(88,204,2,0.2)'
                      : 'none',
                  }}
                >
                  {plan.popular && (
                    <div
                      className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-white text-xs font-black"
                      style={{ background: plan.color }}
                    >
                      🌟 POPULIARIAUSIA
                    </div>
                  )}

                  <h3 className="text-xl font-black text-gray-800 mb-1">
                    {plan.name}
                  </h3>

                  <div className="flex items-baseline gap-1 mb-5">
                    <span
                      className="text-4xl font-black"
                      style={{ color: plan.color }}
                    >
                      {plan.price}
                    </span>

                    <span className="text-gray-400 font-bold">
                      {plan.period}
                    </span>
                  </div>

                  <ul className="space-y-2.5 mb-6">
                    {plan.features.map((f, fi) => (
                      <li
                        key={fi}
                        className="flex items-center gap-2 text-sm font-bold text-gray-700"
                      >
                        <span style={{ color: plan.color }}>
                          ✓
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/register"
                    className="block text-center px-5 py-3 rounded-2xl font-black text-sm transition-all hover:scale-105"
                    style={plan.ctaStyle}
                  >
                    {plan.cta}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────── */}

        <section
          className="py-20 px-4"
          style={{ background: '#F7F7F7' }}
        >
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-gray-800 mb-3">
                Dažniausiai užduodami klausimai apie SmartPanda
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((f, i) => (
                <FaqItem
                  key={i}
                  q={f.q}
                  a={f.a}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── GALUTINIS CTA ─────────────────────────────────────────── */}

        <section
          className="py-24 px-4"
          style={{
            background:
              'linear-gradient(135deg, #58CC02 0%, #1CB0F6 100%)',
          }}
        >
          <div className="max-w-3xl mx-auto text-center">
            <div
              className="text-7xl mb-6 animate-bounce"
              aria-hidden="true"
            >
              🐼
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              Pradėkite šiandien!
            </h2>

            <p className="text-green-100 font-semibold text-lg mb-8 max-w-xl mx-auto">
              Jokios kreditinės kortelės. Jokių įsipareigojimų.
              Tik mokymasis – žaidžiant.
            </p>

            <Link
              to="/register"
              className="inline-block px-10 py-5 rounded-2xl font-black text-xl transition-all hover:scale-105"
              style={{
                background: 'white',
                color: '#58CC02',
                boxShadow: '0 8px 0 rgba(0,0,0,0.15)',
              }}
            >
              Sukurti paskyrą nemokamai 🚀
            </Link>
          </div>
        </section>

        {/* ── FOOTER ────────────────────────────────────────────────── */}

        <footer
          className="py-12 px-4"
          style={{ background: '#1F1F1F' }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl" aria-hidden="true">
                    🐼
                  </span>

                  <span className="text-lg font-black text-white">
                    SmartPanda
                  </span>
                </div>

                <p className="text-gray-500 text-sm font-semibold leading-relaxed">
                  AI edukacinė platforma vaikams nuo 4 iki 14 metų.
                  Mokykitės žaisdami su interaktyviais žaidimais ir
                  personalizuotomis AI užduotimis.
                </p>
              </div>

              {[
                {
                  title: 'Platforma',
                  links: [
                    ['Funkcijos', '#features'],
                    ['Žaidimai', '#games'],
                    ['Kainos', '/subscription'],
                  ],
                },
                {
                  title: 'Paskyra',
                  links: [
                    ['Prisijungti', '/login'],
                    ['Registruotis', '/register'],
                  ],
                },
                {
                  title: 'Teisinė',
                  links: [
                    ['Sąlygos', '/taisykles'],
                    ['Privatumas', '/privatumas'],
                    ['Kontaktai', '/kontaktai'],
                  ],
                },
              ].map((col) => (
                <div key={col.title}>
                  <h4 className="text-white font-black mb-3 text-sm">
                    {col.title}
                  </h4>

                  <ul className="space-y-2">
                    {col.links.map(([label, href]) => (
                      <li key={label}>
                        {href.startsWith('/') ? (
                          <Link
                            to={href}
                            className="text-gray-500 hover:text-gray-300 transition text-sm font-semibold"
                          >
                            {label}
                          </Link>
                        ) : (
                          <a
                            href={href}
                            className="text-gray-500 hover:text-gray-300 transition text-sm font-semibold"
                          >
                            {label}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
              <p className="text-gray-600 text-sm font-semibold">
                © 2026 SmartPanda. Visos teisės saugomos.
              </p>

              <p className="text-gray-600 text-sm font-semibold">
                Powered by Gemini AI 🤖
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;

